import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { MapPin, Plus, Trash2, Edit, Home, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = { label: '', recipient_name: '', phone: '', address: '', city: '', province: '', postal_code: '', is_default: false };

export default function CustomerAddresses() {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchData = () => {
    api.get('/customer/addresses')
      .then(r => setAddresses(r.data.addresses || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialog(true); };
  const openEdit = (addr) => { setEditing(addr); setForm({ ...addr }); setDialog(true); };

  const handleSave = async () => {
    if (!form.recipient_name || !form.address || !form.city) { toast.error('Lengkapi nama penerima, alamat, dan kota'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/customer/addresses/${editing.id}`, form);
        toast.success('Alamat diperbarui');
      } else {
        await api.post('/customer/addresses', form);
        toast.success('Alamat ditambahkan');
      }
      setDialog(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan alamat');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus alamat ini?')) return;
    try {
      await api.delete(`/customer/addresses/${id}`);
      toast.success('Alamat dihapus');
      fetchData();
    } catch (e) {
      toast.error('Gagal menghapus alamat');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await api.put(`/customer/addresses/${id}/default`);
      toast.success('Alamat utama diperbarui');
      fetchData();
    } catch (e) {
      toast.error('Gagal mengatur alamat utama');
    }
  };

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--store-primary, #2D6CDF)]" />
    </div>
  );

  return (
    <div data-testid="customer-addresses-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Alamat Pengiriman</h1>
          <p className="text-sm text-slate-500 mt-1">Kelola alamat pengiriman Anda</p>
        </div>
        <Button onClick={openCreate} className="bg-[var(--store-primary, #2D6CDF)] hover:bg-[var(--store-primary-hover, #1A5BC4)]">
          <Plus size={16} className="mr-2" /> Tambah Alamat
        </Button>
      </div>

      {addresses.length === 0 ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="py-16 text-center">
            <MapPin size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="font-medium text-slate-900 mb-1">Belum ada alamat tersimpan</h3>
            <p className="text-sm text-slate-500 mb-4">Tambahkan alamat untuk mempercepat proses checkout</p>
            <Button onClick={openCreate} className="bg-[var(--store-primary, #2D6CDF)] hover:bg-[var(--store-primary-hover, #1A5BC4)]">
              <Plus size={16} className="mr-2" /> Tambah Alamat Pertama
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {addresses.map(addr => (
            <Card key={addr.id} className={`border-slate-200 shadow-sm ${addr.is_default ? 'ring-2 ring-[var(--store-primary, #2D6CDF)]' : ''}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Home size={16} className={addr.is_default ? 'text-[var(--store-primary, #2D6CDF)]' : 'text-slate-400'} />
                    <span className="font-semibold text-slate-900 text-sm">{addr.label || 'Rumah'}</span>
                    {addr.is_default && <Badge className="bg-blue-100 text-[var(--store-primary, #2D6CDF)] text-xs">Utama</Badge>}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-[var(--store-primary, #2D6CDF)]" onClick={() => openEdit(addr)}>
                      <Edit size={14} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-500" onClick={() => handleDelete(addr.id)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
                <p className="font-medium text-slate-800">{addr.recipient_name}</p>
                <p className="text-sm text-slate-500">{addr.phone}</p>
                <p className="text-sm text-slate-600 mt-1">{addr.address}</p>
                <p className="text-sm text-slate-600">{addr.city}, {addr.province} {addr.postal_code}</p>
                {!addr.is_default && (
                  <Button variant="outline" size="sm" className="mt-3 text-xs h-7" onClick={() => handleSetDefault(addr.id)}>
                    Jadikan Utama
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Alamat' : 'Tambah Alamat Baru'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Label Alamat</Label>
                <Input value={form.label} onChange={e => setField('label', e.target.value)} placeholder="Rumah, Kantor, dll" className="mt-1" />
              </div>
              <div>
                <Label>Nama Penerima <span className="text-red-500">*</span></Label>
                <Input value={form.recipient_name} onChange={e => setField('recipient_name', e.target.value)} placeholder="Nama lengkap" className="mt-1" />
              </div>
            </div>
            <div>
              <Label>Nomor HP</Label>
              <Input value={form.phone} onChange={e => setField('phone', e.target.value)} placeholder="08xxxxxxxxxx" className="mt-1" />
            </div>
            <div>
              <Label>Alamat Lengkap <span className="text-red-500">*</span></Label>
              <Input value={form.address} onChange={e => setField('address', e.target.value)} placeholder="Jl. Contoh No. 123, RT/RW 001/002, Kelurahan..." className="mt-1" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Kota <span className="text-red-500">*</span></Label>
                <Input value={form.city} onChange={e => setField('city', e.target.value)} placeholder="Jakarta" className="mt-1" />
              </div>
              <div>
                <Label>Provinsi</Label>
                <Input value={form.province} onChange={e => setField('province', e.target.value)} placeholder="DKI Jakarta" className="mt-1" />
              </div>
              <div>
                <Label>Kode Pos</Label>
                <Input value={form.postal_code} onChange={e => setField('postal_code', e.target.value)} placeholder="12345" className="mt-1" />
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.is_default} onChange={e => setField('is_default', e.target.checked)} />
              <span className="text-sm text-slate-700">Jadikan alamat utama</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Batal</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[var(--store-primary, #2D6CDF)] hover:bg-[var(--store-primary-hover, #1A5BC4)]">
              {saving ? <><Loader2 size={16} className="mr-2 animate-spin" /> Menyimpan...</> : 'Simpan Alamat'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
